import { useMemo } from "react";
import type { Channel, ChannelMemberResponse } from "stream-chat";
import type { AccessRoleCode } from "Models/AccessRoleCodes";
import { ChannelKind } from "Types/Chat/channelKind";
import { Resources, useResource } from "Translations/Resources";
import { getChannelDisplayName } from "Utils/Chat/getChannelDisplayName";
import { useDisclosedNamesMapQuery } from "Api/Queries/Social/useDisclosedNamesMapQuery";
import { resolveMemberNames } from "Utils/Chat/resolveMemberNames";

type ChatChannelDisplayInfo = {
  displayName: string;
  avatarImage: string | undefined;
  isGroup: boolean;
  isOnline: boolean;
  accessRole: AccessRoleCode | undefined;
  otherMembers: Array<ChannelMemberResponse>;
  /**
   * The channel is frozen, so its counterpart is deliberately unidentifiable (ADR-0008).
   * Consumers must treat this as "show nothing", not as "no value yet": an absent avatar here is a
   * decision, and a `??` fallback to a vendor-computed image would undo it.
   */
  isAnonymized: boolean;
};

export const useChatChannelDisplayInfo = (
  channel: Channel,
  currentUserId: string | undefined,
): ChatChannelDisplayInfo => {
  const { t } = useResource();
  const members = channel.state.members;
  const { data: disclosedNames } = useDisclosedNamesMapQuery();

  // A frozen channel means the pair was cut off (ADR-0008). Both sides see it, so neither learns
  // anything the other doesn't — the flag says the conversation is closed, never why.
  const isAnonymized = channel.data?.frozen === true;
  const anonymousName = t(Resources.Common.AnonymousUser);

  // Names are resolved ONCE here, so every consumer of this hook (channel header, channel list
  // preview, avatar-with-name) shows the same viewer-relative identity (ADR-0007) without each
  // one repeating the rule.
  const otherMembers = useMemo(
    () =>
      resolveMemberNames(
        Object.values(members ?? {}).filter(
          ({ user_id }) => user_id !== currentUserId,
        ),
        disclosedNames,
        { isAnonymized, anonymousName },
      ),
    [members, currentUserId, disclosedNames, isAnonymized, anonymousName],
  );

  const channelKind = channel.data?.channelKind;
  const isGroup = channelKind
    ? channelKind === ChannelKind.Group
    : otherMembers.length > 1;

  const unnamedChannelName = t(Resources.Common.UnnamedChannel);
  const channelName = channel.data?.name;
  const displayName = useMemo(
    () =>
      getChannelDisplayName({
        channelName,
        otherMembers,
        anonymousName,
        unnamedChannelName,
        isGroup,
      }),
    [channelName, otherMembers, anonymousName, unnamedChannelName, isGroup],
  );
  const avatarImage = useMemo(() => {
    if (!isGroup) {
      return otherMembers[0]?.user?.image ?? undefined;
    }
    return undefined;
  }, [isGroup, otherMembers]);

  // Presence and the role badge both leak identity back out of an anonymised channel — a live
  // green dot says the person is here, and the badge narrows them to a merchant or a hunter.
  const isOnline =
    !isGroup && !isAnonymized && (otherMembers[0]?.user?.online ?? false);
  const accessRole =
    !isGroup && !isAnonymized ? otherMembers[0]?.user?.accessRole : undefined;

  return {
    displayName,
    avatarImage,
    isGroup,
    isOnline,
    accessRole,
    otherMembers,
    isAnonymized,
  };
};
